import * as React from "react";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { BankAccountIdType } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountIdType";
import { IncomePaymentDestination } from "../../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { DrawdownPaymentDestinationAllocation } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentDestinationAllocation";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { BankDetailsContent } from "../../Resource/Content";

interface IBankDetailsProps extends IProcessComponentProps {
    ownerHierarchyId: string;
    allBankAccounts: BankAccount[];
    incomePaymentAllocations: DrawdownPaymentDestinationAllocation[];
    onChange: (newAllocations: DrawdownPaymentDestinationAllocation[]) => void;
}
interface IBankDetailsState {
    splitAcrossTwo: boolean;
}

type BankAccountDetails = {
    type: BankAccountIdType;
    id: number;
    accountNumber: number;
    destination: IncomePaymentDestination;
    displayString: string;
    incomeAllocation?: number;
};

// Bank account number can only be 8 digits so nine nines will always be unique and last
const dummyWrapCashBankAccountNumber = 999999999;

export class BankDetails extends ProcessComponent<IBankDetailsProps, IBankDetailsState> {
    constructor(props: any) {
        super(props);
        this.state = { splitAcrossTwo: false };
    }

    componentDidMount() {
        super.componentDidMount();
        const { incomePaymentAllocations } = this.props;
        if (incomePaymentAllocations.length > 1) {
            this.setState({ splitAcrossTwo: true });
        }
    }

    private handleBankAccountChange(bankAccountDetails: BankAccountDetails, bankAccountIndex: number) {
        const { incomePaymentAllocations, onChange } = this.props;

        if (bankAccountDetails === null || bankAccountDetails.destination === null) {
            incomePaymentAllocations.pop();
        } else {
            if (incomePaymentAllocations.length > bankAccountIndex) {
                incomePaymentAllocations[bankAccountIndex] = {
                    BankAccountIdType: bankAccountDetails.type,
                    BankAccountId: bankAccountDetails.id,
                    Destination: bankAccountDetails.destination,
                    Allocation: bankAccountDetails.incomeAllocation,
                };
            } else {
                incomePaymentAllocations.push({
                    BankAccountIdType: bankAccountDetails.type,
                    BankAccountId: bankAccountDetails.id,
                    Destination: bankAccountDetails.destination,
                    Allocation: bankAccountDetails.incomeAllocation,
                });
            }
        }

        onChange(incomePaymentAllocations);
    }

    public render(): JSX.Element {
        const { allBankAccounts, incomePaymentAllocations } = this.props;

        const bankAccounts: Array<BankAccountDetails> = [
            {
                type: null,
                id: null,
                accountNumber: null,
                destination: null,
                displayString: "Please select",
            },
        ];

        allBankAccounts?.forEach((account) => {
            if (account.NominatedForWithdrawal) {
                bankAccounts.push({
                    type: BankAccountIdType.BankAccount,
                    id: account.BankAccountId,
                    accountNumber: Number(account.AccountNumber),
                    destination: IncomePaymentDestination.BankAccount,
                    displayString: `${account.SortCode} | ${account.AccountNumber} | ${account.AccountName}`,
                });
            }
        });

        bankAccounts.push({
            type: null,
            id: null,
            accountNumber: dummyWrapCashBankAccountNumber,
            destination: IncomePaymentDestination.WrapCash,
            displayString: "Cash Account",
        });

        const bankAccountOptions = bankAccounts.map((account) => account.accountNumber);

        let bankAccount1Details: BankAccountDetails;
        if (incomePaymentAllocations.length > 0) {
            bankAccount1Details = bankAccounts.find((details) => {
                if (details.destination === IncomePaymentDestination.WrapCash) {
                    return incomePaymentAllocations[0].Destination === IncomePaymentDestination.WrapCash;
                } else {
                    return (
                        incomePaymentAllocations[0].Destination === IncomePaymentDestination.BankAccount &&
                        details.type === incomePaymentAllocations[0].BankAccountIdType &&
                        details.id === incomePaymentAllocations[0].BankAccountId
                    );
                }
            });
            bankAccount1Details.incomeAllocation = incomePaymentAllocations[0].Allocation;
        }

        let bankAccount2Details: BankAccountDetails;
        if (incomePaymentAllocations.length > 1) {
            bankAccount2Details = bankAccounts.find((details) => {
                if (details.destination === IncomePaymentDestination.WrapCash) {
                    return incomePaymentAllocations[1].Destination === IncomePaymentDestination.WrapCash;
                } else {
                    return (
                        incomePaymentAllocations[1].Destination === IncomePaymentDestination.BankAccount &&
                        details.type === incomePaymentAllocations[1].BankAccountIdType &&
                        details.id === incomePaymentAllocations[1].BankAccountId
                    );
                }
            });
            bankAccount2Details.incomeAllocation = incomePaymentAllocations[1].Allocation;
        }

        return (
            <>
                <div className="h4 mb-10" data-test="">
                    {BankDetailsContent.labels.selectBankAccount}
                </div>
                {/* 2 - one please select, one bank account */}
                {bankAccounts.length > 2 && (
                    <div className="row">
                        <div className="col-12">
                            <CheckboxEditor
                                label={BankDetailsContent.labels.splitAcrossTwoBankAccounts}
                                value={this.state.splitAcrossTwo}
                                onChange={(v) => {
                                    !v && this.handleBankAccountChange(null, 1);
                                    this.setState({ splitAcrossTwo: v });
                                }}
                                cssWrapper="square-checkbox"
                            />
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-4">
                        <SelectEditor
                            label={this.state.splitAcrossTwo ? BankDetailsContent.labels.bankAccount1 : ""}
                            options={bankAccountOptions}
                            selected={bankAccount1Details && bankAccount1Details.accountNumber}
                            getText={(v) => {
                                return bankAccounts.find((option) => option.accountNumber === v).displayString;
                            }}
                            onChange={(v) => {
                                const bankAccountDetails = bankAccounts.find((account) => account.accountNumber === v);
                                this.handleBankAccountChange(bankAccountDetails, 0);
                            }}
                            required={"Please select a bank account"}
                        />
                    </div>
                </div>
                {this.state.splitAcrossTwo && (
                    <>
                        <div className="row">
                            <div className="col-4">
                                <PercentageTextBoxEditor
                                    label={BankDetailsContent.labels.proportionOfIncome}
                                    value={bankAccount1Details && bankAccount1Details.incomeAllocation}
                                    atMost={{
                                        errorMessage: BankDetailsContent.messages.percentageOver100,
                                        value: 100,
                                    }}
                                    atLeast={{
                                        errorMessage: BankDetailsContent.messages.percentageUnder0,
                                        value: 0,
                                    }}
                                    onChange={(v) => {
                                        bankAccount1Details.incomeAllocation = v;
                                        this.handleBankAccountChange(bankAccount1Details, 0);
                                    }}
                                    required={"Please enter a percentage"}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-4">
                                <SelectEditor
                                    label={BankDetailsContent.labels.bankAccount2}
                                    options={
                                        bankAccount1Details
                                            ? bankAccountOptions.filter((accountNumber) => {
                                                  return (
                                                      bankAccount1Details &&
                                                      accountNumber !== bankAccount1Details.accountNumber
                                                  );
                                              })
                                            : bankAccountOptions
                                    }
                                    selected={bankAccount2Details && bankAccount2Details.accountNumber}
                                    getText={(v) => {
                                        return bankAccounts.find((option) => option.accountNumber === v).displayString;
                                    }}
                                    onChange={(v) => {
                                        const bankAccountDetails = bankAccounts.find(
                                            (account) => account.accountNumber === v
                                        );
                                        this.handleBankAccountChange(bankAccountDetails, 1);
                                    }}
                                    required={"Please select a bank account"}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-4">
                                <PercentageTextBoxEditor
                                    label={BankDetailsContent.labels.proportionOfIncome}
                                    value={bankAccount2Details && bankAccount2Details.incomeAllocation}
                                    atMost={{
                                        errorMessage: BankDetailsContent.messages.percentageOver100,
                                        value: 100,
                                    }}
                                    atLeast={{
                                        errorMessage: BankDetailsContent.messages.percentageUnder0,
                                        value: 0,
                                    }}
                                    onChange={(v) => {
                                        bankAccount2Details.incomeAllocation = v;
                                        this.handleBankAccountChange(bankAccount2Details, 1);
                                    }}
                                    validationRules={[
                                        {
                                            errorMessage: "",
                                            validator: (v) => {
                                                return bankAccount1Details?.incomeAllocation
                                                    ? v + bankAccount1Details?.incomeAllocation === 100
                                                    : false;
                                            },
                                        },
                                    ]}
                                />
                                {bankAccount1Details?.incomeAllocation + bankAccount2Details?.incomeAllocation !==
                                    100 && (
                                    <Alert
                                        message={BankDetailsContent.messages.totalProportionOfIncomeNot100}
                                        alertType={AlertType.Error}
                                    />
                                )}
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    }
}

export default BankDetails;
